"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type { FormEvent } from "react";
import ImageAreaEditor from "@/components/image-area-editor";
import { useToast } from "@/components/toast-provider";
import { formatMXN, getPriceBreakdown } from "@/lib/pricing";
import { clamp, getCropGeometry, type ImageCrop } from "@/lib/image-crop";
import type {
  CustomizationGroup,
  GalleryImage,
  ImageArea,
  Product,
} from "@/lib/types";
import {
  addLocalCartItem,
  getLocalCartItem,
  updateLocalCartItem,
} from "@/lib/local-cart";
import {
  deleteProductDraft,
  loadProductDraft,
  saveProductDraft,
} from "@/lib/configuration-drafts";
import {
  getAreaAspectRatio,
  getCropAreaSize,
  getCropResolution,
  getFaceWindow,
  LOW_RESOLUTION_WARNING_DPI,
} from "@/lib/print";

type CropState = ImageCrop & {
  file: File;
  previewUrl: string;
};

type ProductConfiguratorProps = {
  product: Product;
  groups: CustomizationGroup[];
  galleryImages: GalleryImage[];
  editCartItemId?: string;
};

const MAX_CLIENT_FILE_BYTES = 12 * 1024 * 1024;

function getInitialSelections(groups: CustomizationGroup[]) {
  return Object.fromEntries(
    groups.map((group) => [
      group.id,
      group.required ? (group.values[0]?.id ?? "") : "",
    ]),
  );
}

async function getAllowedImageType(file: File) {
  const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "image/png";
  }
  if (
    String.fromCharCode(...bytes.slice(0, 4)) === "RIFF" &&
    String.fromCharCode(...bytes.slice(8, 12)) === "WEBP"
  ) {
    return "image/webp";
  }
  return null;
}

function uploadErrorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : "No se pudieron procesar las imágenes. Inténtalo de nuevo.";
}

export default function ProductConfigurator({
  product,
  groups,
  galleryImages,
  editCartItemId,
}: ProductConfiguratorProps) {
  const router = useRouter();
  const { notify } = useToast();
  const [selections, setSelections] = useState<Record<string, string>>(() =>
    getInitialSelections(groups),
  );
  const [artwork, setArtwork] = useState<Record<string, CropState>>({});
  const [cartSubmitting, setCartSubmitting] = useState(false);
  const [cartUpdated, setCartUpdated] = useState(false);
  const [error, setError] = useState("");
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [draftStatus, setDraftStatus] = useState("Cargando avance guardado…");
  const [editingCartItemLoaded, setEditingCartItemLoaded] = useState(false);
  const isEditingCartItem = Boolean(editCartItemId);
  const objectUrls = useRef(new Set<string>());
  const saveTimer = useRef<number | null>(null);
  const saveRevision = useRef(0);
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  const price = useMemo(
    () => getPriceBreakdown(product, groups, selections),
    [groups, product, selections],
  );

  useEffect(
    () => () => {
      for (const url of objectUrls.current) {
        URL.revokeObjectURL(url);
      }
      objectUrls.current.clear();
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    async function restoreDraft() {
      const restoredUrls: string[] = [];
      setDraftLoaded(false);
      setEditingCartItemLoaded(false);
      try {
        const cartItem = editCartItemId
          ? await getLocalCartItem(editCartItemId)
          : null;
        if (editCartItemId && !cartItem) {
          setError("Este producto ya no está en el carrito.");
          setDraftStatus("No se pudo abrir el diseño del carrito.");
          return;
        }
        if (cartItem && cartItem.product.id !== product.id) {
          setError("Este diseño pertenece a otro producto y no se puede editar aquí.");
          setDraftStatus("No se pudo abrir el diseño del carrito.");
          return;
        }

        const draft = cartItem ?? (await loadProductDraft(product.id));
        if (!draft) {
          setDraftStatus(
            "El avance se guardará en este dispositivo. Los datos de contacto no se guardan.",
          );
          return;
        }

        const restoredSelections = Object.fromEntries(
          groups.map((group) => {
            const savedValue = group.values.find(
              (value) => value.id === draft.selections[group.id],
            );
            return [
              group.id,
              savedValue?.id ?? (group.required ? (group.values[0]?.id ?? "") : ""),
            ];
          }),
        );
        const restoredArtwork: Record<string, CropState> = {};
        let skippedArtwork = false;

        for (const area of product.imageAreas) {
          const saved = draft.artwork[area.id];
          if (!saved) {
            continue;
          }
          if (saved.file.size === 0 || saved.file.size > MAX_CLIENT_FILE_BYTES) {
            skippedArtwork = true;
            continue;
          }
          let mimeType: string | null;
          try {
            mimeType = await getAllowedImageType(saved.file);
          } catch (imageError) {
            console.error("Failed to validate saved product artwork.", imageError);
            skippedArtwork = true;
            continue;
          }
          if (!mimeType) {
            skippedArtwork = true;
            continue;
          }

          const file = saved.file.type === mimeType
            ? saved.file
            : new File([saved.file], saved.file.name, {
                type: mimeType,
                lastModified: saved.file.lastModified,
              });
          const previewUrl = URL.createObjectURL(file);
          restoredUrls.push(previewUrl);
          restoredArtwork[area.id] = {
            file,
            previewUrl,
            x: clamp(saved.x, -1, 1),
            y: clamp(saved.y, -1, 1),
            zoom: clamp(saved.zoom, 1, 3),
            sourceWidth: 0,
            sourceHeight: 0,
          };
        }

        if (cancelled) {
          for (const url of restoredUrls) {
            URL.revokeObjectURL(url);
          }
          return;
        }
        for (const url of restoredUrls) {
          objectUrls.current.add(url);
        }
        setSelections(restoredSelections);
        setArtwork(restoredArtwork);
        setDraftDirty(true);
        setEditingCartItemLoaded(Boolean(cartItem));
        setDraftStatus(
          skippedArtwork
            ? cartItem
              ? "Se abrió el diseño del carrito, pero no se pudo recuperar una de las imágenes."
              : "Se recuperó el avance, pero no se pudo recuperar una de las imágenes."
            : cartItem
              ? "Se abrió el diseño guardado en tu carrito."
              : "Se recuperó tu avance guardado en este dispositivo.",
        );
      } catch (draftError) {
        console.error("Failed to restore the local product draft.", draftError);
        if (editCartItemId) {
          setError(uploadErrorMessage(draftError));
          setDraftStatus("No se pudo abrir el diseño del carrito.");
        } else {
          setDraftStatus(
            "No se pudo recuperar el avance guardado. Puedes continuar y agregar el diseño al carrito.",
          );
        }
      } finally {
        if (!cancelled) {
          setDraftLoaded(true);
        }
      }
    }

    void restoreDraft();
    return () => {
      cancelled = true;
    };
  }, [editCartItemId, groups, product]);

  useEffect(() => {
    if (isEditingCartItem || !draftLoaded || !draftDirty) {
      return;
    }

    const revision = ++saveRevision.current;
    const draft = {
      productId: product.id,
      updatedAt: new Date().toISOString(),
      selections,
      artwork: Object.fromEntries(
        Object.entries(artwork).map(([areaId, crop]) => [
          areaId,
          { file: crop.file, x: crop.x, y: crop.y, zoom: crop.zoom },
        ]),
      ),
    };
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      setDraftStatus("Guardando el avance en este dispositivo…");
      const save = saveQueue.current.then(
        () => saveProductDraft(draft),
        () => saveProductDraft(draft),
      );
      saveQueue.current = save;
      void save
        .then(() => {
          if (revision === saveRevision.current) {
            setDraftStatus("Avance guardado en este dispositivo.");
          }
        })
        .catch((saveError) => {
          if (revision === saveRevision.current) {
            console.error("Failed to save the local product draft.", saveError);
            setDraftStatus(
              "No se pudo guardar el avance en este dispositivo. Mantén esta página abierta.",
            );
          }
        });
    }, 500);

    return () => {
      if (saveTimer.current !== null) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
    };
  }, [artwork, draftDirty, draftLoaded, isEditingCartItem, product.id, selections]);

  const removeSavedDraft = async () => {
    saveRevision.current += 1;
    if (saveTimer.current !== null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    await saveQueue.current.catch(() => undefined);
    await deleteProductDraft(product.id);
  };

  const updateCrop = (
    areaId: string,
    updates: Partial<
      Pick<CropState, "x" | "y" | "zoom" | "sourceWidth" | "sourceHeight">
    >,
  ) => {
    setArtwork((current) => {
      const item = current[areaId];
      return item ? { ...current, [areaId]: { ...item, ...updates } } : current;
    });
    setDraftDirty(true);
  };

  const selectImage = async (area: ImageArea, file: File) => {
    if (file.size > MAX_CLIENT_FILE_BYTES) {
      const message = "Cada imagen debe pesar menos de 12 MB.";
      setError(message);
      notify("error", message);
      return;
    }

    let mimeType: string | null;
    try {
      mimeType = await getAllowedImageType(file);
    } catch (imageError) {
      const message = uploadErrorMessage(imageError);
      setError(message);
      notify("error", message);
      return;
    }
    if (!mimeType) {
      const message = "Elige una imagen JPG, PNG o WebP.";
      setError(message);
      notify("error", message);
      return;
    }

    const normalizedFile = file.type === mimeType
      ? file
      : new File([file], file.name, {
          type: mimeType,
          lastModified: file.lastModified,
        });
    setError("");
    setDraftDirty(true);
    const previewUrl = URL.createObjectURL(normalizedFile);
    objectUrls.current.add(previewUrl);
    const previous = artwork[area.id];
    if (previous) {
      URL.revokeObjectURL(previous.previewUrl);
      objectUrls.current.delete(previous.previewUrl);
    }
    setArtwork((current) => {
      return {
        ...current,
          [area.id]: {
            file: normalizedFile,
            previewUrl,
            x: 0,
            y: 0,
            zoom: 1,
            sourceWidth: 0,
            sourceHeight: 0,
          },
        };
    });
  };

  const addToCart = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setCartUpdated(false);

    if (isEditingCartItem && !editingCartItemLoaded) {
      const message = "No se pudo abrir este diseño del carrito. Regresa al carrito e inténtalo de nuevo.";
      setError(message);
      notify("error", message);
      return;
    }

    const missingGroup = groups.find(
      (group) => group.required && !selections[group.id],
    );
    if (missingGroup) {
      const message = `Selecciona una opción para ${missingGroup.name}.`;
      setError(message);
      notify("error", message);
      return;
    }
    const missingArea = product.imageAreas.find(
      (area) => area.required && !artwork[area.id],
    );
    if (missingArea) {
      const message = `Agrega la imagen de ${missingArea.label.toLocaleLowerCase("es-MX")}.`;
      setError(message);
      notify("error", message);
      return;
    }

    setCartSubmitting(true);
    try {
      const updatedItem = {
        product,
        groups,
        selections,
        artwork: Object.fromEntries(
          Object.entries(artwork).map(([areaId, crop]) => [
            areaId,
            { file: crop.file, x: crop.x, y: crop.y, zoom: crop.zoom },
          ]),
        ),
      };
      if (editCartItemId) {
        await updateLocalCartItem(editCartItemId, updatedItem);
        notify("success", "Se actualizaron los cambios de tu diseño.");
        router.push("/cart");
      } else {
        await addLocalCartItem(updatedItem);
        setSelections(getInitialSelections(groups));
        setArtwork({});
        for (const url of objectUrls.current) {
          URL.revokeObjectURL(url);
        }
        objectUrls.current.clear();
        setDraftDirty(false);
        setDraftStatus("Tu siguiente diseño empezará sin imágenes ni opciones guardadas.");
        try {
          await removeSavedDraft();
        } catch (draftError) {
          console.error("Failed to clear the local product draft after adding to cart.", draftError);
          setDraftDirty(true);
          setDraftStatus(
            "El diseño se agregó al carrito, pero no se pudo borrar el avance anterior.",
          );
        }
        setCartUpdated(true);
        notify("success", "Se agregó tu diseño al carrito de este navegador.");
      }
    } catch (cartError) {
      const message = uploadErrorMessage(cartError);
      setError(message);
      notify("error", message);
    } finally {
      setCartSubmitting(false);
    }
  };

  if (!draftLoaded) {
    return (
      <div className="draft-loading" role="status">
        Cargando el avance guardado en este dispositivo…
      </div>
    );
  }

  return (
    <form className="configurator-layout" onSubmit={addToCart}>
      <div className="configurator-main">
        <section className="form-section">
          <div className="form-section-heading">
            <span className="step-number">01</span>
            <div>
              <span className="eyebrow">A TU MANERA</span>
              <h2>Elige tus materiales.</h2>
            </div>
          </div>
          <div className="option-grid">
            {groups.map((group) => (
              <label className="field option-field" key={group.id}>
                <span className="field-label">{group.name}</span>
                {group.description && (
                  <span className="field-help">{group.description}</span>
                )}
                <select
                  value={selections[group.id] ?? ""}
                  required={group.required}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setSelections((current) => ({
                      ...current,
                      [group.id]: value,
                    }));
                    setDraftDirty(true);
                  }}
                >
                  {!group.required && <option value="">Sin preferencia</option>}
                  {group.values.map((value) => (
                    <option key={value.id} value={value.id}>
                      {value.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-heading">
            <span className="step-number">02</span>
            <div>
              <span className="eyebrow">TU DISEÑO</span>
              <h2>Haz espacio para tus ideas.</h2>
              <p className="section-description">
                Sube una imagen y ajusta el encuadre directamente sobre ella.
              </p>
            </div>
          </div>
          <div className="image-area-grid">
            {product.imageAreas.map((area, index) => {
              const crop = artwork[area.id];
              const cropArea = getCropAreaSize(area);
              const cropWindow = crop?.sourceWidth && crop.sourceHeight
                ? getCropGeometry(
                    area,
                    crop,
                    crop.sourceWidth,
                    crop.sourceHeight,
                  ).window
                : null;
              const cropResolution = crop && cropWindow
                ? getCropResolution(
                    crop.sourceWidth,
                    crop.sourceHeight,
                    cropWindow.width,
                    cropWindow.height,
                    area,
                  )
                : null;
              return (
                <div className="image-area-card" key={area.id}>
                  <div className="image-area-title">
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <strong>{area.label}</strong>
                    <span>{area.required ? "OBLIGATORIA" : "OPCIONAL"}</span>
                  </div>
                  <ImageAreaEditor
                    area={area}
                    src={crop?.previewUrl ?? null}
                    crop={crop ?? null}
                    onCropChange={(updates) => updateCrop(area.id, updates)}
                    onSelectFile={(file) => void selectImage(area, file)}
                    galleryImages={galleryImages}
                    onImageError={() => {
                      const message = "No se pudo abrir la imagen seleccionada.";
                      setError(message);
                      notify("error", message);
                    }}
                    showPreview={false}
                  />
                  {cropResolution &&
                    cropResolution.dpi < LOW_RESOLUTION_WARNING_DPI && (
                      <p className="crop-resolution-warning" role="status">
                        Este recorte ({cropResolution.width} × {cropResolution.height} px)
                        tiene menos de {LOW_RESOLUTION_WARNING_DPI} ppp al imprimir a{" "}
                        {cropArea.widthCm} × {cropArea.heightCm} cm de imagen y puede verse borroso.
                        Usa una imagen de mayor resolución o amplía el área de recorte.
                      </p>
                    )}
                </div>
              );
            })}
          </div>
          {product.imageAreas.length === 0 && (
            <div className="no-image-areas">
              Este producto no requiere imágenes personalizadas.
            </div>
          )}
        </section>

        <section className="privacy-note">
          <span aria-hidden="true">✳</span>
          <div>
            <p>
              Tus imágenes y el carrito se guardan en este navegador. Los
              datos de contacto y envío se solicitan durante el checkout.
            </p>
            <p className="draft-status" role="status">{draftStatus}</p>
            {draftDirty && (
              <button
                className="draft-clear-button"
                type="button"
                onClick={() => {
                  setDraftDirty(false);
                  setDraftStatus("Borrando el avance guardado…");
                  void removeSavedDraft()
                    .then(() => {
                      setDraftStatus("Se borró el avance de este dispositivo.");
                    })
                    .catch((draftError) => {
                      console.error("Failed to remove the local product draft.", draftError);
                      setDraftStatus("No se pudo borrar el avance guardado.");
                    });
                }}
              >
                Borrar avance guardado de este dispositivo
              </button>
            )}
          </div>
        </section>
      </div>

      <aside className="order-sidebar">
        <div className="order-card">
          <span className="eyebrow">TU PIEZA</span>
          <h2>{isEditingCartItem ? "Edita tu diseño." : product.name}</h2>
          {isEditingCartItem && <p className="order-category">{product.name}</p>}
          <p className="order-category">{product.category} · PERSONALIZABLE</p>
          <div className="preview-stack">
            {product.imageAreas.slice(0, 2).map((area) => {
              const crop = artwork[area.id];
              const cropWindow = crop?.sourceWidth && crop.sourceHeight
                ? getCropGeometry(
                    area,
                    crop,
                    crop.sourceWidth,
                    crop.sourceHeight,
                  ).window
                : null;
              const faceWindow = cropWindow
                ? getFaceWindow(area, cropWindow)
                : null;
              return (
                <div
                  className={`preview-page preview-page-${area.shape}${faceWindow ? " preview-page-with-artwork" : ""}`}
                  key={area.id}
                  style={{ aspectRatio: String(getAreaAspectRatio(area)) }}
                >
                  {crop && faceWindow ? (
                    // Uploaded blobs bypass Next Image so crop sizing stays explicit.
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={crop.previewUrl}
                      alt=""
                      width={crop.sourceWidth}
                      height={crop.sourceHeight}
                      style={{
                        left: `${(-faceWindow.left / faceWindow.width) * 100}%`,
                        top: `${(-faceWindow.top / faceWindow.height) * 100}%`,
                        width: `${100 / faceWindow.width}%`,
                        height: `${100 / faceWindow.height}%`,
                      }}
                    />
                  ) : (
                    <span className="preview-monogram">R</span>
                  )}
                  <span className="preview-label">{area.label}</span>
                </div>
              );
            })}
            {product.imageAreas.length > 2 && (
              <span className="more-areas">
                + {product.imageAreas.length - 2} áreas de imagen
              </span>
            )}
          </div>
          <div className="price-lines">
            <div>
              <span>Precio base</span>
              <span>{formatMXN(product.basePriceCents)}</span>
            </div>
            {price.selectedOptions.map((option) => (
              <div key={option.groupId}>
                <span>{option.valueLabel}</span>
                <span>
                  {option.appliedCents === 0
                    ? "Incluido"
                    : `${option.appliedCents > 0 ? "+" : "−"}${formatMXN(Math.abs(option.appliedCents))}`}
                </span>
              </div>
            ))}
          </div>
          <div className="price-total">
            <span>Total estimado</span>
            <strong>{formatMXN(price.totalCents)}</strong>
          </div>
          <p className="shipping-disclaimer">
            El envío se cotiza por separado. Esta versión de muestra no procesa
            pagos.
          </p>
          {cartUpdated && (
            <Link className="builder-cart-link" href="/cart">
              Ir al carrito <span aria-hidden="true">↗</span>
            </Link>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {isEditingCartItem && (
            <Link className="text-button configurator-cancel-edit" href="/cart">
              Cancelar y volver al carrito
            </Link>
          )}
          <button
            className="button button-dark order-submit"
            type="submit"
            disabled={cartSubmitting || (isEditingCartItem && !editingCartItemLoaded)}
          >
            {cartSubmitting
              ? isEditingCartItem
                ? "Guardando…"
                : "Agregando…"
              : isEditingCartItem
                ? "Guardar cambios"
                : "Agregar al carrito"}
            {!cartSubmitting && <span aria-hidden="true">↗</span>}
          </button>
          <span className="secure-note">
            <span aria-hidden="true">✳</span> Hecho especialmente para ti
          </span>
        </div>
      </aside>
    </form>
  );
}
